import CryptoJS from 'crypto-js';

/**
 * JWT-клиент аутентификации и вспомогательные утилиты работы с токенами.
 * Предоставляет функции получения/обновления токена, проверки срока и получения пользователя.
 */

/**
 * Возвращает заголовок Authorization с токеном в формате Bearer.
 * @param {string} token - JWT-токен.
 * @returns {Object} Объект заголовков.
 */
export const getAuthHeadersApi = (token) => {
  return { Authorization: token ? `Bearer ${token}` : '' };
};

/**
 * Формирует конфигурацию fetch c JWT-токеном.
 * @param {string} method - HTTP-метод.
 * @param {string} token - JWT-токен.
 * @returns {Object} Конфиг запроса для fetch.
 */
export const getFetchConfigApi = (method, token) => ({
  method: method,
  mode: 'cors',
  credentials: 'include',
  headers: {
    'Content-type': 'application/json',
    ...getAuthHeadersApi(token),
  },
});

/**
 * Декодирует payload JWT без проверки подписи.
 * @param {string} token - JWT-токен.
 * @returns {Object} Полезная нагрузка токена.
 * @throws {Error} При неверном формате токена.
 */
const decodeJwtToken = (token) => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Ошибка декодирования токена: ', error);
    throw new Error('Неверный формат токена');
  }
};

/**
 * Возвращает время истечения токена в мс.
 * @param {string} token - JWT-токен.
 * @returns {number} Таймстамп истечения или 0 при ошибке.
 */
export const getTokenExpiration = (token) => {
  if (!token) return 0;
  try {
    const payload = decodeJwtToken(token);
    return payload.exp * 1000; // конвертируем в миллисекунды
  } catch {
    return 0;
  }
};

/**
 * Проверяет, истёк ли срок действия JWT.
 * @param {string} token - JWT-токен.
 * @returns {boolean} true, если токен просрочен.
 */
const isTokenExpired = (token) => {
  try {
    const payload = decodeJwtToken(token);
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Ошибка проверки срока действия токена: ', error.message);
    return true;
  }
};

// Проверка токена с использованием библитеки jsonwebtoken. Проверка на подпись и срок действия.

// /**
//  * Проверяет JWT токен и возвращает его полезную нагрузку.
//  * @param {string} token - JWT токен.
//  * @param {string} secretOrPublicKey - Секретный ключ или открытый ключ для проверки подписи.
//  * @returns {Object} Полезная нагрузка токена.
//  * @throws {Error} Ошибка при проверке подписи токена.
//  */
// const verifyJwtToken = (token, secretOrPublicKey) => {
//     try {
//         const decoded = jwt.verify(token, secretOrPublicKey);
//         return decoded;
//     } catch (error) {
//         console.error('Ошибка проверки подписи токена: ', error);
//         throw new Error('Неверная подпись токена');
//     }
// }

/**
 * Хеширует пароль без соли с использованием алгоритма SHA256. Используется библиотека crypto-js.
 * @param {string} password - Пароль для хеширования.
 * @returns {string} Хешированный пароль.
 */
const hashPassword = (password) => {
  return CryptoJS.SHA256(password).toString(CryptoJS.enc.Base64);
};

/**
 * Получает JWT токен, отправляя запрос на указанный endpoint с переданными учетными данными (логин + пароль).
 * @param {string} endpoint - Адрес, куда делать запрос для аутентификации.
 * @param {Object} credentials - Объект с учетными данными { login: password }.
 * @returns {Promise<string>} Промис, который разрешается токеном.
 * @throws {Error} Ошибка получения токена.
 */
const getToken = async (endpoint, credentials) => {
  try {
    const config = getFetchConfigApi('POST', null);
    config.body = JSON.stringify(credentials);

    let response = await fetch(endpoint, config);

    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Неверный логин или пароль!');
      } else {
        throw new Error(`Ошибка аутентификации: ${response.statusText}`);
      }
    }

    let data = await response.json();
    if (!data.access_token) {
      throw new Error('Токен не найден в ответе сервера!');
    }

    return data.access_token;
  } catch (error) {
    console.error('Ошибка получения токена: ', error);
    throw error;
  }
};

/**
 * Обновляет access token используя refresh token из cookies
 * @returns {Promise<string>} Новый access token
 */
const refreshToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_AUTH_URL}/refresh`,
    //   'http://localhost:8000/auth/refresh',
      getFetchConfigApi('POST', null),
    );

    if (!response.ok) {
      throw new Error('Ошибка обновления токена');
    }

    const data = await response.json();
    return data.access_token;
  } catch (error) {
    console.error('Ошибка обновления токена: ', error);
    throw error;
  }
};

/**
 * Выполняет аутентификацию, отправляя запрос на указанный endpoint с переданными учетными данными.
 * @param {string} endpoint - Адрес, куда делать запрос для аутентификации.
 * @param {string} credentials - Строка с учетными данными "login:password".
 * @returns {Promise<Object>} Промис, который разрешается объектом с токеном.
 * @throws {Error} Ошибка аутентификации.
 */
export const client = async (endpoint, credentials = '') => {
  try {
    // Для логина используем логин и пароль
    if (credentials.login && credentials.password) {
      const { login, password } = credentials;
      // const hashedPassword = hashPassword(password);

      let token = await getToken(endpoint, {
        login,
        password: password,
      });

      if (isTokenExpired(token)) {
        throw new Error('Токен истек!');
      }

      const payload = decodeJwtToken(token);
      return { token, user: payload };
    }
    // Для обновления токена
    else if (endpoint.includes('refresh')) {
      const token = await refreshToken();
      return { token };
    }
  } catch (error) {
    console.error('Ошибка в client(): ', error);
    throw error;
  }
};

/**
 * Получает информацию о текущем пользователе
 * @returns {Promise<Object>} Промис с данными пользователя
 */
export const getCurrentUser = async () => {
  try {
    const token = localStorage.getItem('auth-token');
    if (!token) throw new Error('Токен не найден');

    const config = {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    };

    // const response = await fetch('http://localhost:8000/users/me', config);
    const response = await fetch(`${process.env.REACT_APP_API_URL}/users/me`, config);

    if (!response.ok) {
      if (response.status === 401) {
        // Попробуем обновить токен
        const newToken = await refreshToken();
        localStorage.setItem('auth-token', newToken);

        // Повторяем запрос с новым токеном
        config.headers.Authorization = `Bearer ${newToken}`;
        const newResponse = await fetch(`${process.env.REACT_APP_API_URL}/users/me`, config);

        if (!newResponse.ok) {
          throw new Error('Ошибка авторизации');
        }

        return await newResponse.json();
      }
      throw new Error(`Ошибка: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Ошибка получения данных пользователя: ', error);
    throw error;
  }
};

// export default client;

